import { useApp } from 'contexts/app';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { omit } from 'lodash';
import { createContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import submissionService from 'services/submission';
import { getTemplatedFeedback, stripHTML } from 'utils/helper';

const PageContext = createContext();

const PageProvider = (props) => {
    const { currentAssignment, submissions, setSubmissions } = useApp();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const { submissionId } = useParams();
    const { message } = useSnackbar();

    const [tab, setTab] = useState('grades');
    const [selectedCriterionId, setSelectedCriterionId] = useState(null);
    const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [form, setForm] = useState(null);
    const [showDetails, setShowDetails] = useState(false);
    const [isSuggestionVisible, setIsSuggestionVisible] = useState(false);
    const [isDocumentFullScreen, setIsDocumentFullScreen] = useState(false);
    const [contentBankResourcesUsageDialog, setContentBankResourcesUsageDialog] = useState(false);

    const viewableSubmissions = submissions
        ?.filter(({ grading }) => grading)
        .sort((a, b) => {
            const aName = a.data.name.toLowerCase();
            const bName = b.data.name.toLowerCase();
            if (aName < bName) return -1;
            if (aName > bName) return 1;
            return 0;
        });
    const currentSubmission = viewableSubmissions?.find(({ id }) => id === submissionId);

    const selectedCriterionTitle = currentAssignment?.rubric?.criteria?.find(
        ({ id }) => id === selectedCriterionId
    )?.title;

    const getHighlights = () => {
        let highlights;
        if (tab === 'grades') {
            highlights = currentSubmission?.ai?.grading?.[selectedCriterionId]?.locations || [];
        }
        if (tab === 'feedback') {
            if (currentSubmission.ai.feedback.feedback_type === 'rubric') {
                highlights =
                    form.feedback?.improvement_suggestions?.find(({ id }) => id === selectedFeedbackId)?.negative
                        ?.locations || [];
            } else {
                highlights =
                    form.feedback?.improvement_suggestions?.find(({ id }) => id === selectedFeedbackId)?.locations ||
                    [];
            }
        }
        return highlights.map((item) => ({ id: selectedFeedbackId, value: item }));
    };

    const onClickBack = () => {
        history.push(`/assignments/${currentAssignment.id}/submissions`);
    };

    const onChangeSubmission = (_, { props: { value: submissionId } }) => {
        history.push(`/assignments/${currentAssignment.id}/submissions/${submissionId}`);
    };

    const viewPrevSubmission = () => {
        const index = viewableSubmissions.findIndex(({ id }) => id === currentSubmission.id);
        const prevSubmission = viewableSubmissions[index - 1];
        history.push(`/assignments/${currentAssignment.id}/submissions/${prevSubmission.id}`);
    };

    const viewNextSubmission = () => {
        const index = viewableSubmissions.findIndex(({ id }) => id === currentSubmission.id);
        const nextSubmission = viewableSubmissions[index + 1];
        history.push(`/assignments/${currentAssignment.id}/submissions/${nextSubmission.id}`);
    };

    const onClickEdit = () => {
        setForm(currentSubmission.grading);
        setIsEditing(true);
    };

    const onClickEditCancel = () => {
        setDialog({
            open: true,
            message: 'Discard Changes?',
            description: 'If you continue, your changes will be lost. To save them, please click “Save.”',
            note: '',
            variant: 'destructive',
            actionButtonText: 'Discard',
            cancelButtonText: 'Cancel',
            onAction: () => {
                setForm(currentSubmission.grading);
                setIsEditing(false);
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const onClickEditSave = () => {
        const updatedForm =
            currentSubmission.ai?.feedback?.feedback_type === 'templated'
                ? {
                      ...form,
                      feedback: {
                          ...form.feedback,
                          commentary: getTemplatedFeedback(currentAssignment, form),
                      },
                  }
                : form;
        const updatedSubmission = {
            ...omit(currentSubmission, ['_id']),
            modified_on: new Date().getTime(),
            grading: updatedForm,
        };
        setSubmissions((current) => {
            return current.map((submission) => {
                return submission.id === updatedSubmission.id ? updatedSubmission : submission;
            });
        });
        submissionService.updateSubmission(updatedSubmission.id, updatedSubmission);
        setIsEditing(false);
    };

    const onSelect = async ({ key }) => {
        if (key === 'copy-all') {
            let feedback = '';
            if (typeof currentSubmission.grading?.feedback === 'string') {
                feedback += stripHTML(form.feedback);
            } else if (currentSubmission.ai?.feedback?.feedback_type === 'rubric') {
                form.feedback.improvement_suggestions.forEach(({ criterion_id, positive, negative }, index) => {
                    if (index) feedback += '\n\n';
                    const criterion = currentAssignment.rubric.criteria.find(({ id }) => id === criterion_id);
                    feedback += `${criterion.title}\n\n`;
                    feedback += 'What you did well\n\n';
                    feedback += !positive.active ? 'This feedback was deleted.' : stripHTML(positive.text);
                    feedback += '\n\nHow you can improve\n\n';
                    feedback += !negative.active ? 'This feedback was deleted.' : stripHTML(negative.text);
                });
            } else if (currentSubmission.ai?.feedback?.feedback_type === 'system') {
                feedback += stripHTML(form.feedback.commentary);
                form.feedback.improvement_suggestions
                    .filter(({ active }) => active)
                    .forEach(({ text }) => {
                        feedback += '\n\n';
                        feedback += stripHTML(text);
                    });
            }
            await navigator.clipboard.writeText(feedback);
            message.success('Copied to clipboard.');
        }
        if (key === 'delete') {
            setDialog({
                open: true,
                message: 'Delete All Feedback?',
                description: 'Are you sure you want to delete all feedback? This can’t be undone.',
                note: '',
                variant: 'destructive',
                actionButtonText: 'Delete',
                cancelButtonText: 'Cancel',
                onAction: () => {
                    setForm((current) => {
                        const updatedForm = {
                            ...current,
                            feedback: {
                                commentary: '',
                                improvement_suggestions: current.feedback.improvement_suggestions.map((item) => {
                                    if (currentSubmission.ai?.feedback?.feedback_type === 'rubric') {
                                        return {
                                            ...item,
                                            positive: {
                                                ...item.positive,
                                                active: false,
                                            },
                                            negative: {
                                                ...item.negative,
                                                active: false,
                                            },
                                        };
                                    }
                                    if (currentSubmission.ai?.feedback?.feedback_type === 'system') {
                                        return {
                                            ...item,
                                            active: false,
                                        };
                                    }
                                    return item;
                                }),
                            },
                        };
                        const updatedSubmission = {
                            ...currentSubmission,
                            modified_on: new Date().getTime(),
                            grading: updatedForm,
                        };
                        setSubmissions((submissions) => {
                            return submissions.map((submission) => {
                                if (submission.id !== updatedSubmission.id) return submission;
                                return updatedSubmission;
                            });
                        });
                        submissionService.updateSubmission(updatedSubmission.id, omit(updatedSubmission, ['_id']));
                        return updatedForm;
                    });
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }
    };

    const updateFeedback = (updatedForm) => {
        const updatedSubmission = {
            ...currentSubmission,
            modified_on: new Date().getTime(),
            grading: updatedForm,
        };
        setSubmissions((submissions) => {
            return submissions.map((submission) => {
                if (submission.id !== updatedSubmission.id) return submission;
                submissionService.updateSubmission(updatedSubmission.id, omit(updatedSubmission, ['_id']));
                return updatedSubmission;
            });
        });
    };

    useEffect(() => {
        if (!currentSubmission) return;
        setForm(currentSubmission.grading);
        setSelectedCriterionId(currentAssignment.rubric.criteria[0].id);
        if (['rubric', 'system'].includes(currentSubmission.grading.feedback?.feedback_type)) {
            const activeSuggestions = currentSubmission.grading.feedback.improvement_suggestions.filter(
                ({ active }) => active
            );
            if (activeSuggestions[0]) {
                setSelectedFeedbackId(activeSuggestions[0].id);
            }
        }
    }, [currentAssignment, currentSubmission]);

    return (
        <PageContext.Provider
            value={{
                currentAssignment,
                viewableSubmissions,
                currentSubmission,
                selectedCriterionTitle,
                highlights: getHighlights(),
                tab,
                setTab,
                selectedCriterionId,
                setSelectedCriterionId,
                selectedFeedbackId,
                setSelectedFeedbackId,
                isEditing,
                form,
                setForm,
                showDetails,
                setShowDetails,
                isSuggestionVisible,
                setIsSuggestionVisible,
                isDocumentFullScreen,
                setIsDocumentFullScreen,
                contentBankResourcesUsageDialog,
                setContentBankResourcesUsageDialog,
                onClickBack,
                onChangeSubmission,
                viewPrevSubmission,
                viewNextSubmission,
                onClickEdit,
                onClickEditCancel,
                onClickEditSave,
                onSelect,
                updateFeedback,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
